import React, { Fragment } from 'react';
import Typography from '@material-ui/core/Typography';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { MESSAGE_DIRECTION_PARAM } from 'features/message-center/constants';
import { useChatScroll } from 'features/message-center/hooks';
import { getDateLabel } from 'features/message-center/utils';
import isEmpty from 'lodash/isEmpty';
import strings from 'strings';
import LoaderContent from 'UI/components/templates/DrawerContentLayout/LoaderContent';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { UIStatus } from 'UI/constants/status';
import { SMSEmptyState, SvgNoResults } from 'UI/res';

import {
  AbsoluteContainer,
  ChatContainer,
  DateTitle,
  JumpToLatestButton
} from './ChatMessages.styled';
import { Message } from './components';

const COPIES = strings.messageCenter;

const ListMessages = ({ messages = [], onClickRetry }) => {
  return messages.map(message => (
    <Message
      key={`${message?.messageId}-${message.date}`}
      onClickRetry={() => onClickRetry(message)}
      {...message}
    />
  ));
};

export const ChatMessages = ({
  chat,
  chatStatus,
  onClickRetry,
  onFetchMoreMessages,
  shouldScrollToBottom
}) => {
  const isLoading = chatStatus === UIStatus.Loading;

  const handleScrollToTop = () => {
    if (isLoading) return;

    onFetchMoreMessages(MESSAGE_DIRECTION_PARAM.DESC);
  };

  const handleScrollToBottom = () => {
    if (isLoading) return;

    onFetchMoreMessages(MESSAGE_DIRECTION_PARAM.ASC);
  };

  const { chatContainerRef, chatEndRef, showJumpButton, scrollToBottom } = useChatScroll({
    chat,
    onScrollBottom: handleScrollToBottom,
    onScrollTop: handleScrollToTop,
    shouldScrollToBottom
  });

  if (chatStatus === UIStatus.Error)
    return (
      <EmptyPlaceholder
        title={strings.shared.errors.serverError.title}
        subtitle={strings.shared.errors.serverError.subtitle}
        customEmptyState={<SvgNoResults height={400} />}
      />
    );

  if (!isLoading && isEmpty(chat)) {
    return (
      <EmptyPlaceholder
        title={COPIES.emptyState.title}
        subtitle={COPIES.emptyState.subtitle}
        customEmptyState={<SMSEmptyState height={400} />}
      />
    );
  }

  return (
    <>
      {isLoading && (
        <AbsoluteContainer>
          <LoaderContent color="primary" height={100} />
        </AbsoluteContainer>
      )}
      <ChatContainer ref={chatContainerRef}>
        {Object.keys(chat).map(day => (
          <Fragment key={day}>
            <DateTitle variant="body2">{getDateLabel(day)}</DateTitle>
            <ListMessages messages={chat[day]} onClickRetry={onClickRetry} />
          </Fragment>
        ))}
        <div ref={chatEndRef} />
      </ChatContainer>

      <JumpToLatestButton
        endIcon={<ArrowDownwardIcon />}
        onClick={() => scrollToBottom('smooth')}
        showJumpButton={showJumpButton}
        variant="contained"
      >
        <Typography variant="body2">{COPIES.chat.goToLastMessage}</Typography>
      </JumpToLatestButton>
    </>
  );
};
